import React, { useCallback, useMemo } from 'react'

import { Button, ButtonClean, ModalBox, useModalContext } from '@fourthwall/components'

import { SubscriptionStorageLabel } from '../../components/SubscriptionStorageLabel/SubscriptionStorageLabel'
import { UpgradeSubscriptionButton } from '../../components/UpgradeSubscriptionButton/UpgradeSubscriptionButton'
import { useSubscription } from '../../hooks/useSubscription'
import { useSubscriptionStorageLimits } from '../../hooks/useSubscriptionStorageLimits'
import { AddSubscriptionModal } from '../AddSubscriptionModal/AddSubscriptionModal'
import * as S from './SubscriptionDigitalStorageLimitModal.styled'

export const SubscriptionDigitalStorageLimitModal = () => {
  const { open, close } = useModalContext()
  const { canUpgradeSubscription } = useSubscription()
  const { digitalProductsStorage } = useSubscriptionStorageLimits()

  const openAddSubscriptionModal = useCallback(() => {
    open(AddSubscriptionModal, {
      source: 'product_details',
    })
  }, [open])

  const title = useMemo(() => {
    if (canUpgradeSubscription) {
      return (
        <>
          You've reached your storage limit of{' '}
          <SubscriptionStorageLabel size={digitalProductsStorage.limit} />. Upgrade to Pro for{' '}
          <SubscriptionStorageLabel size={digitalProductsStorage.topSubscriptionPlanLimit} />{' '}
          storage.
        </>
      )
    }

    return (
      <>
        You've reached your storage limit of{' '}
        <SubscriptionStorageLabel size={digitalProductsStorage.limit} />. Contact us for unlimited
        storage.
      </>
    )
  }, [
    canUpgradeSubscription,
    digitalProductsStorage.limit,
    digitalProductsStorage.topSubscriptionPlanLimit,
  ])

  const description = useMemo(() => {
    if (canUpgradeSubscription) {
      return (
        <>
          Uploading this file would put you past your storage limit of{' '}
          <SubscriptionStorageLabel size={digitalProductsStorage.limit} />. You've uploaded{' '}
          <SubscriptionStorageLabel size={digitalProductsStorage.usage} /> of digital file storage.
          You can archive your digital products to free space or upgrade to Pro for{' '}
          <SubscriptionStorageLabel size={digitalProductsStorage.topSubscriptionPlanLimit} />{' '}
          storage.
        </>
      )
    }

    return (
      <>
        Uploading this file would put you past your storage limit of{' '}
        <SubscriptionStorageLabel size={digitalProductsStorage.limit} />. You've uploaded{' '}
        <SubscriptionStorageLabel size={digitalProductsStorage.usage} /> of digital file storage.
        You can archive your digital products to free space or email us at{' '}
        <ButtonClean to="mailto:support@fourthwall.com" label="support@fourthwall.com" /> to request
        a higher limit.
      </>
    )
  }, [
    canUpgradeSubscription,
    digitalProductsStorage.limit,
    digitalProductsStorage.usage,
    digitalProductsStorage.topSubscriptionPlanLimit,
  ])

  return (
    <ModalBox title={title} onClose={close}>
      <S.Content>
        {description}
        {canUpgradeSubscription && (
          <S.Footer>
            <Button label="Close" size="small" appearance="secondary" onClick={close} />
            <UpgradeSubscriptionButton
              source="product_details"
              onClick={openAddSubscriptionModal}
            />
          </S.Footer>
        )}
      </S.Content>
    </ModalBox>
  )
}
